export type DevStatus = "done" | "in_progress" | "partial" | "planned";

export type DevTask = {
  id: string;
  title: string;
  status: DevStatus;
  note?: string;
};

export type DevModule = {
  id: string;
  title: string;
  description?: string;
  status: DevStatus;
  progress: number;
  tasks: DevTask[];
};

export type DevPlatform = {
  id: string;
  title: string;
  subtitle: string;
  status: DevStatus;
  progress: number;
  modules: DevModule[];
};

export const STATUS_LABEL: Record<DevStatus, string> = {
  done: "ເຮັດແລ້ວ",
  in_progress: "ກຳລັງເຮັດ",
  partial: "ບາງສ່ວນ",
  planned: "ຍັງບໍ່ເລີ່ມ",
};

export const STATUS_ORDER: DevStatus[] = [
  "done",
  "partial",
  "in_progress",
  "planned",
];

export const DEVELOPMENT_PLATFORMS: DevPlatform[] = [
  {
    id: "admin",
    title: "Admin Web",
    subtitle:
      "ສູນຄວບຄຸມ platform — ຮ້ານຄ້າ, Catalog, ການກວດກາ, ກະເປົາເງິນ",
    status: "partial",
    progress: 72,
    modules: [
      {
        id: "admin-a1",
        title: "A1 — ຈັດການຮ້ານຄ້າ",
        description: "ຈັດການຮ້ານ, ຢືນຢັນຕົວຕົນ, ສາຂາ, ປະສິດທິພາບຮ້ານ",
        status: "done",
        progress: 95,
        tasks: [
          {
            id: "a1-list",
            title: "ລາຍການຮ້ານ + ລາຍລະອຽດ",
            status: "done",
          },
          {
            id: "a1-verify",
            title: "ຢືນຢັນຮ້ານ (Verify)",
            status: "done",
          },
          { id: "a1-branches", title: "ພາບລວມສາຂາ", status: "done" },
          {
            id: "a1-products",
            title: "ສິນຄ້າຕໍ່ຮ້ານ (Shop Products)",
            status: "done",
          },
          {
            id: "a1-performance",
            title: "ລາຍງານປະສິດທິພາບຮ້ານ",
            status: "partial",
            note: "ພື້ນຖານມີແລ້ວ — ranking ຂັ້ນສູງຍັງບໍ່ຄົບ",
          },
        ],
      },
      {
        id: "admin-a2",
        title: "A2 — ຈັດການຊ່າງແປງ",
        description: "ຄິວລົງທະບຽນຊ່າງ, ຢືນຢັນຕົວຕົນ, ລະງັບ/ແບນ",
        status: "done",
        progress: 90,
        tasks: [
          { id: "a2-queue", title: "ຄິວລົງທະບຽນຊ່າງ", status: "done" },
          {
            id: "a2-detail",
            title: "ລາຍລະອຽດ + ຢືນຢັນຕົວຕົນ",
            status: "done",
          },
          { id: "a2-performance", title: "ປະສິດທິພາບຊ່າງ", status: "partial" },
        ],
      },
      {
        id: "admin-a3",
        title: "A3 — ຄ່າທຳນຽມ & ຄອມມິດຊັນ",
        description:
          "ຄ່າທຳນຽມ Marketplace, ຊ່າງ, ຕໍ່ຫມວດ, override ຕໍ່ຮ້ານ",
        status: "done",
        progress: 85,
        tasks: [
          { id: "a3-rules", title: "ກົດຄ່າທຳນຽມ + CRUD", status: "done" },
          { id: "a3-category", title: "ຄ່າທຳນຽມຕໍ່ຫມວດ", status: "done" },
          { id: "a3-override", title: "Override ຕໍ່ຮ້ານ", status: "partial" },
        ],
      },
      {
        id: "admin-a4",
        title: "A4 — ການກວດກາ Marketplace",
        description: "ກວດ listing, ລາຍງານ, ປະຫວັດການກວດກາ",
        status: "done",
        progress: 90,
        tasks: [
          {
            id: "a4-listings",
            title: "ລາຍການ listing + ສ້າງ/ເຜີຍແຜ່",
            status: "done",
          },
          { id: "a4-reports", title: "ລະບົບລາຍງານ", status: "done" },
          { id: "a4-history", title: "ປະຫວັດການກວດກາ", status: "done" },
          {
            id: "a4-auto",
            title: "ການກວດກາອັດຕະໂນມັດ",
            status: "planned",
          },
        ],
      },
      {
        id: "admin-a5",
        title: "A5 — ຈັດການຂໍ້ພິພາດ",
        description: "ຂໍ້ພິພາດຄຳສັ່ງຊື້ / ງານຊ່າງ",
        status: "done",
        progress: 85,
        tasks: [
          { id: "a5-list", title: "ລາຍການຂໍ້ພິພາດ", status: "done" },
          {
            id: "a5-resolve",
            title: "ແກ້ໄຂ + ບັນທຶກ resolution",
            status: "done",
          },
          { id: "a5-fraud", title: "ກວດຈັບການທຸຈຳກິດ", status: "planned" },
        ],
      },
      {
        id: "admin-a6",
        title: "A6 — ວິເຄາະ platform",
        description: "Dashboard GMV, ຄຳສັ່ງ, ຮ້ານ/ຊ່າງ active",
        status: "done",
        progress: 80,
        tasks: [
          { id: "a6-dashboard", title: "ແຜງຄວບຄຸມ + ກຣາຟ", status: "done" },
          {
            id: "a6-alerts",
            title: "ແຈ້ງເຕືອນການກວດກາ",
            status: "partial",
          },
        ],
      },
      {
        id: "admin-a7",
        title: "A7 — ການບໍລິຫານລະບົບ",
        description: "ບົດບາດ, ສິດ, audit log",
        status: "done",
        progress: 90,
        tasks: [
          { id: "a7-roles", title: "ຈັດການບົດບາດ + ສິດ", status: "done" },
          { id: "a7-users", title: "ຈັດການຜູ້ໃຊ້ Admin", status: "done" },
          { id: "a7-audit", title: "Audit logs", status: "done" },
        ],
      },
      {
        id: "admin-a8",
        title: "A8 — ກະເປົາເງິນ & Settlement",
        description:
          "ກະເປົາເງິນ, ເຕີມເງິນ, ຄືນເງິນ, ຈ່າຍອອກ, reconciliation",
        status: "done",
        progress: 88,
        tasks: [
          { id: "a8-overview", title: "ພາບລວມກະເປົາເງິນ", status: "done" },
          {
            id: "a8-accounts",
            title: "ບັນຊີ Customer/Shop/Mechanic",
            status: "done",
          },
          { id: "a8-tx", title: "ລາຍການທຸລະກຳ", status: "done" },
          { id: "a8-topup", title: "ຄຳຂໍເຕີມເງິນ", status: "done" },
          { id: "a8-refund", title: "ຄືນເງິນ", status: "done" },
          { id: "a8-payout", title: "ການຈ່າຍເງິນອອກ", status: "done" },
          {
            id: "a8-compliance",
            title: "Compliance & Risk",
            status: "done",
          },
          { id: "a8-recon", title: "Reconciliation", status: "done" },
        ],
      },
      {
        id: "admin-catalog",
        title: "Catalog (C1–C6)",
        description: "Master catalog, ມອບສິນຄ້າໃຫ້ຮ້ານ, Vehicle/EPC",
        status: "partial",
        progress: 75,
        tasks: [
          {
            id: "c1-master",
            title: "C1 — Master Catalog CRUD",
            status: "done",
          },
          {
            id: "c2-assign",
            title: "C2 — ມອບສິນຄ້າໃຫ້ຮ້ານ + ລາຄາ/SKU/barcode",
            status: "done",
          },
          {
            id: "c3-requests",
            title: "C3 — ຄຳຂໍເພີ່ມສິນຄ້າຈາກ POS",
            status: "partial",
            note: "ຕາຕະລາງມີແລ້ວ — UI ອະນຸມັດຍັງບໍ່ຄົບ",
          },
          {
            id: "c4-epc",
            title: "C4 — EPC Vehicle Catalog Editor",
            status: "done",
          },
          {
            id: "c5-model-code",
            title: "C5 — Vehicle Model Code",
            status: "done",
          },
          {
            id: "c6-config",
            title: "C6 — Vehicle Configurations",
            status: "partial",
            note: "Migration + Admin UI ບາງສ່ວນ",
          },
          {
            id: "c-fitment",
            title: "ຄິວ fitment / POS provision",
            status: "done",
          },
          { id: "c-sku-media", title: "ອັບໂຫຼດ SKU Media", status: "done" },
        ],
      },
      {
        id: "admin-wh-db",
        title: "Shared DB — Phase W (warehouse locations)",
        description:
          "Migration + seed ສຳລັບ Merchant POS — ຮັນຈາກ Admin_Web",
        status: "done",
        progress: 100,
        tasks: [
          {
            id: "wh-0018",
            title: "0018 — warehouse_zones / locations / balances",
            status: "done",
          },
          {
            id: "wh-0019",
            title: "0019 — seed MAIN + MAIN-DEFAULT ຕໍ່ສາຂາ",
            status: "done",
          },
          {
            id: "wh-0020",
            title: "0020 — goods_receipt_lines.warehouse_location_id",
            status: "done",
          },
          {
            id: "wh-seed",
            title: "Script seed:warehouse-locations (+ --report)",
            status: "done",
          },
          {
            id: "wh-flag",
            title: "Feature flag warehouse_locations ໃນ Admin",
            status: "done",
          },
        ],
      },
    ],
  },
  {
    id: "merchant-pos",
    title: "Merchant POS",
    subtitle:
      "ລະບົບຂາຍໜ້າຮ້ານ — POS, ສຕ໋ອກ, ສາຂາ, sync Marketplace",
    status: "partial",
    progress: 74,
    modules: [
      {
        id: "pos-core",
        title: "Phase 1–2 — ຂາຍ & ສຕ໋ອກພື້ນຖານ",
        status: "done",
        progress: 95,
        tasks: [
          {
            id: "pos-checkout",
            title: "POS checkout (ເງິນສົດ/ໂອນ/wallet)",
            status: "done",
          },
          { id: "pos-inventory", title: "ສຕ໋ອກຕໍ່ສາຂາ", status: "done" },
          {
            id: "pos-branch",
            title: "Multi-branch + ໂອນສຕ໋ອກ",
            status: "done",
          },
          { id: "pos-staff", title: "ພະນັກງານ + ບົດບາດ", status: "done" },
          {
            id: "pos-split",
            title: "Split payment (ເງິນສົດ + wallet)",
            status: "done",
          },
        ],
      },
      {
        id: "pos-catalog",
        title: "Catalog & ຫມວດສິນຄ້າໃນ POS",
        status: "done",
        progress: 92,
        tasks: [
          {
            id: "pos-cat-tabs",
            title: "ແຖບຫມວດ + combobox ໃນໜ້າຂາຍ",
            status: "done",
          },
          {
            id: "pos-cat-top",
            title: "ຫມວດຍອດນິຍົມ + ຈື່ filter",
            status: "done",
          },
          {
            id: "pos-cat-inv",
            title: "ສະແດງຫມວດໃນໜ້າສິນຄ້າ/ສາງ",
            status: "done",
          },
          {
            id: "pos-cat-nested",
            title: "Nested categories (ທາງເລືອກ)",
            status: "planned",
          },
        ],
      },
      {
        id: "pos-ux",
        title: "UX/UI — ໂໝດງ່າຍສຳລັບພະນັກງານ",
        status: "done",
        progress: 100,
        tasks: [
          {
            id: "pos-simple",
            title: "Simple mode + ປຸ່ມໃຫຍ່",
            status: "done",
          },
          {
            id: "pos-nav",
            title: "Sidebar ສັ້ນ + Quick actions",
            status: "done",
          },
          {
            id: "pos-warehouse-ux",
            title: "UX ໜ້າສາງ/ຮັບເຂົ້າ",
            status: "done",
          },
        ],
      },
      {
        id: "pos-warehouse-loc",
        title: "Phase W — ຕຳແໜ່ງສຕ໋ອກໃນສາງ",
        description: "Merchant_Pos/docs/warehouse/DESIGN_WAREHOUSE_LOCATIONS.md",
        status: "done",
        progress: 100,
        tasks: [
          {
            id: "w1-model",
            title: "W1 — Schema + migration + seed",
            status: "done",
            note: "Admin 0018/0019/0020 · seed:warehouse-locations",
          },
          {
            id: "w2-api",
            title: "W2 — API CRUD + assign + lookup + reports §9",
            status: "done",
          },
          {
            id: "w3-ui",
            title: "W3 — UI ຈັດການ + column/filter + QR + scan modal",
            status: "done",
          },
          {
            id: "w4-receive",
            title: "W4 — Receive / PO + GRN warehouse_location_id",
            status: "done",
          },
          {
            id: "w4-transfer",
            title: "W4 — Stock transfer toLocationId",
            status: "done",
          },
          {
            id: "w4-pos",
            title: "W4 — POS hint + FIFO deduct + void คืน balance",
            status: "done",
          },
          {
            id: "w4-reports",
            title: "§9 Reports — unlocated / empty / receive-by-location",
            status: "done",
          },
        ],
      },
      {
        id: "pos-hr",
        title: "Phase HR — ລະບົບບຸກຄະລາກ/ກະ",
        status: "planned",
        progress: 5,
        tasks: [
          {
            id: "hr-staff",
            title: "ພະນັກງານ + ບົດບາດພື້ນຖານ",
            status: "done",
          },
          {
            id: "hr-shift",
            title: "ກະ / ລາ / ເວລາເຂົ້າ-ອອກ",
            status: "planned",
          },
        ],
      },
      {
        id: "pos-marketplace",
        title: "Marketplace Sync (Phase O–S)",
        status: "partial",
        progress: 55,
        tasks: [
          {
            id: "mp-listing",
            title: "ເບິ່ງ listing / ລາຄາ / sync status",
            status: "partial",
          },
          { id: "mp-dispute", title: "ຂໍ້ພິພາດຝັ່ງຮ້ານ", status: "partial" },
          {
            id: "mp-settlement",
            title: "Settlement / payout history",
            status: "partial",
          },
        ],
      },
    ],
  },
  {
    id: "marketplace",
    title: "Marketplace App",
    subtitle: "ແອບລູກຄ້າ — ຄົ້ນຫາ, ຊື້, wallet, EPC diagram",
    status: "partial",
    progress: 55,
    modules: [
      {
        id: "mp-phase-a",
        title: "Phase A — Supply (Listing + Gates)",
        status: "partial",
        progress: 85,
        tasks: [
          {
            id: "mpa-publish",
            title: "Publish listing + fitment/stock gate",
            status: "done",
          },
          {
            id: "mpa-moderation",
            title: "Moderation integration",
            status: "done",
          },
          { id: "mpa-uat", title: "UAT / script ຄົບ", status: "partial" },
        ],
      },
      {
        id: "mp-phase-b",
        title: "Phase B — Customer MVP",
        status: "partial",
        progress: 85,
        tasks: [
          {
            id: "mpb-browse",
            title: "Browse → cart → checkout",
            status: "done",
          },
          { id: "mpb-wallet", title: "Wallet + orders", status: "done" },
          { id: "mpb-fulfill", title: "POS fulfillment", status: "done" },
          {
            id: "mpb-gap",
            title: "Commission + idempotency + UAT",
            status: "planned",
          },
        ],
      },
      {
        id: "mp-phase-c",
        title: "Phase C — Post-MVP",
        status: "partial",
        progress: 40,
        tasks: [
          { id: "mpc-c1", title: "C1 Display price", status: "done" },
          {
            id: "mpc-c2",
            title: "C2 Stock/price sync ຫຼັງ POS",
            status: "done",
          },
          { id: "mpc-c3", title: "C3 Reviews", status: "done" },
          {
            id: "mpc-c4",
            title: "C4 Disputes UI ລູກຄ້າ",
            status: "done",
          },
          {
            id: "mpc-c5",
            title: "C5 Settlement / payout",
            status: "partial",
          },
          { id: "mpc-c8", title: "C8 Notifications", status: "planned" },
          {
            id: "mpc-c6-7",
            title: "C6–C7 Multi-shop / payment ຂະຫຍາຍ",
            status: "planned",
          },
        ],
      },
      {
        id: "mp-epc",
        title: "EPC — Diagram Browse",
        status: "partial",
        progress: 35,
        tasks: [
          {
            id: "epc-admin",
            title: "Admin C4 — ກະກອກຂໍ້ມູນ EPC",
            status: "done",
          },
          {
            id: "epc-e0-e1",
            title: "E0–E1 Schema + public read API",
            status: "planned",
          },
          {
            id: "epc-e2-e3",
            title: "E2–E3 UI diagram + PDP",
            status: "planned",
          },
          {
            id: "epc-e4",
            title: "E4 POS browse (ທາງເລືອກ)",
            status: "planned",
          },
          { id: "epc-e5", title: "E5 VIN decode", status: "planned" },
        ],
      },
    ],
  },
];

export const DEVELOPMENT_META = {
  title: "PARTZYNC — ສະຖານະການພັດທະນາ",
  subtitle: "ຕິດຕາມຄວາມຄືບໜ້າງານ implement ທັງ platform",
  lastUpdated: "2026-06-30",
  legend: STATUS_LABEL,
};

/** ແຈ້ງ deploy ຮອບລ່າສຸດ — ສະແດງເປັນ alert ໜ້າ /development */
export type DeployAlertSection = {
  title: string;
  items: string[];
};

export type DeployAlert = {
  id: string;
  title: string;
  deployedAt: string;
  summary: string;
  sections: DeployAlertSection[];
};

export const DEVELOPMENT_DEPLOY_ALERT: DeployAlert = {
  id: "deploy-2026-06-30-phase-w",
  title: "Deploy ຮອບນີ້ — Phase W: ຕຳແໜ່ງສຕ໋ອກໃນສາງ (v1)",
  deployedAt: "2026-06-30",
  summary:
    "Merchant POS v1 ສຳລັບ warehouse locations ຄົບ — feature ປິດຄ່າເລີ່ມຕົ້ນ; ຕ້ອງເປີດ flag ແລະຮັນ migration ກ່ອນ UAT",
  sections: [
    {
      title: "Merchant POS — ສິ່ງທີ່ເພີ່ມ/ປ່ຽນ",
      items: [
        "CRUD ໂຊນ/ຊ່ອງ + QR label + mobile scan modal (ສະແກນຊ່ອງ → ເບິ່ງ/ເພີ່ມ SKU)",
        "ໜ້າສາງ: ຄອລຳ «ຕຳແໜ່ງ» + filter ຕາມຊ່ອງ · SKU detail ກະจาย qty",
        "ຮັບເຂົ້າ / PO receive: ເລືອກຊ່ອງ (default + override ຕໍ່ບັນທັດ)",
        "ໂອນສາຂາ: toLocationId ປາຍທາງ · GRN ບັນທຶກ warehouse_location_id",
        "POS: badge ຕຳແໜ່ງໃນກະຕ່າ + FIFO ຫຼຸດ balance ຊ່ອງຕອນຂາຍ (void/refund ຄືນ)",
        "ລາຍງານ §9: unlocated · ຊ່ອງວ່າງ · ຮັບເຂົ້າຕາມຊ່ອງ (/reports/warehouse-locations)",
      ],
    },
    {
      title: "Admin / Shared DB",
      items: [
        "Migration 0018–0020 (warehouse tables + GRN column) — ຮັນຈາກ Admin_Web",
        "Feature flag warehouse_locations ໃນ Shop settings (default: ປິດ)",
        "Script: bun run seed:warehouse-locations (--report ສຳລັບ unlocated)",
      ],
    },
    {
      title: "Ops checklist ກ່ອນ/ຫຼັງ deploy",
      items: [
        "Apply migration 0020 ຖ້າຍັງບໍ່ຖືກ apply ບົນ shared DB",
        "Deploy Admin_Web + Merchant_Pos ຄູ່ກັນ (schema align)",
        "Merchant_Pos: bun run test:warehouse (ຕ້ອງມີ DATABASE_URL)",
        "UAT: ເປີດ warehouse_locations ໃຫ້ຮ້ານ demo ຈາກ Admin → Shop → Feature flags",
      ],
    },
  ],
};

export function countTasksByStatus(platforms: DevPlatform[]) {
  const counts: Record<DevStatus, number> = {
    done: 0,
    in_progress: 0,
    partial: 0,
    planned: 0,
  };

  for (const platform of platforms) {
    for (const mod of platform.modules) {
      for (const task of mod.tasks) {
        counts[task.status] += 1;
      }
    }
  }

  return counts;
}

export function overallProgress(platforms: DevPlatform[]): number {
  if (platforms.length === 0) return 0;
  const sum = platforms.reduce((acc, p) => acc + p.progress, 0);
  return Math.round(sum / platforms.length);
}
